import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";

import QrCodePreview from "../Common/QrCodePreview";
import CustomARViewer from "../AR/CustomARViewer";
import { const_var, params } from "../../redux/reducers/reducer";
import { makeUrlMobileReachable } from "../../services/arHosting.service";
import {
    canShareArModelFile,
    downloadArModel,
    exportBuildingSceneToGlb,
    exportBuildingSceneToUsdz,
    getARPlatform,
    getHostedARModelUrls,
    openAndroidSceneViewer,
    shareArModelFile,
} from "../../services/arViewer.service";

/* ─── styles ─────────────────────────────────────────────────────────── */

const cardStyle = {
    border: "1px solid #dfe5df",
    borderRadius: "12px",
    padding: "16px",
    marginBottom: "14px",
    background: "#f9fbf9",
};

const primaryBtn = {
    display: "block",
    width: "100%",
    borderRadius: "10px",
    border: "1px solid #1fac48",
    background: "#1fac48",
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "16px",
    padding: "14px",
    cursor: "pointer",
    marginTop: "12px",
    textAlign: "center",
    textDecoration: "none",
};

const secondaryBtn = {
    ...primaryBtn,
    background: "#ffffff",
    color: "#1fac48",
};

const statusBox = (tone) => ({
    borderRadius: "10px",
    padding: "12px 14px",
    marginTop: "12px",
    fontSize: 14,
    ...(tone === "error"
        ? { background: "#fff7f7", border: "1px solid #efc7c7", color: "#9f1239" }
        : tone === "success"
        ? { background: "#f3fbf5", border: "1px solid #b9e4c4", color: "#166534" }
        : { background: "#f4f8ff", border: "1px solid #bfd4ff", color: "#1d4ed8" }),
});

/* ─── iOS view ───────────────────────────────────────────────────────── */
/**
 * Exports the live Three.js building scene to USDZ in the browser,
 * then renders a native <a rel="ar"> so the user can tap it directly.
 *
 * WHY: iOS Quick Look requires a direct native tap — any JS .click() or
 * async wrapper loses the user-gesture context and closes the camera
 * immediately. We pre-generate the USDZ blob URL up front so the anchor
 * is ready to tap without any async work on tap.
 */
const IosARView = () => {
    const [usdzBlobUrl, setUsdzBlobUrl] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [status, setStatus] = useState(null);
    const blobUrlRef = useRef(null);

    useEffect(() => {
        return () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); };
    }, []);

    const generateUsdz = async () => {
        setIsExporting(true);
        setStatus({ tone: "info", text: "Exporting building — this takes a few seconds…" });
        try {
            const name = (params.building_type_name || "sensei-building")
                .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            const fileName = `${name}-${params.p_w || 0}x${params.p_d || 0}x${params.p_h || 0}.usdz`;
            const { blobUrl, file } = await exportBuildingSceneToUsdz(const_var.scene, fileName, const_var.camera?.position);
            if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = blobUrl;
            const sizeKb = Math.round((file?.size || 0) / 1024);
            setUsdzBlobUrl(blobUrl);
            setStatus({ tone: "success", text: `Ready! (${sizeKb} KB) — Tap "View in AR" below.` });
        } catch (err) {
            setStatus({ tone: "error", text: err?.message || "Could not export the building. Please try again." });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div style={{ padding: "4px 0 8px" }}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
                <div style={{ fontSize: 52 }}>🏗️</div>
                <h5 style={{ margin: "10px 0 4px", fontWeight: 700, color: "#111" }}>
                    View In Your Space
                </h5>
                <p style={{ color: "#555", fontSize: 14, margin: 0 }}>
                    Place your building in the real world using Apple AR.
                </p>
            </div>

            <div style={{ background: "#f4f8ff", border: "1px solid #bfd4ff", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#1e3a6e", lineHeight: 1.8, marginBottom: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>📱 How to place:</div>
                <div>① Tap <strong>"Prepare for AR"</strong> → wait for export</div>
                <div>② Tap <strong>"View in AR"</strong> — camera opens</div>
                <div>③ Point phone at the <strong>floor</strong>, move slowly</div>
                <div>④ <strong>Tap the floor</strong> where you want the building placed</div>
                <div>⑤ Use <strong>two fingers</strong> to move / rotate / resize</div>
            </div>

            {!usdzBlobUrl && (
                <button type="button" style={primaryBtn} onClick={generateUsdz} disabled={isExporting}>
                    {isExporting ? "Exporting building…" : "🏗️ Prepare for AR"}
                </button>
            )}

            {status && <div style={statusBox(status.tone)}>{status.text}</div>}

            {usdzBlobUrl && (
                <>
                    {/*
                     * iOS Quick Look: <img> must be visible (opacity:0, NOT display:none)
                     * otherwise Safari ignores rel="ar" and Quick Look never launches.
                     */}
                    <a
                        rel="ar"
                        href={usdzBlobUrl}
                        style={{ ...primaryBtn, marginTop: 10, position: "relative", display: "block" }}
                    >
                        <img
                            src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=="
                            alt=""
                            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0 }}
                        />
                        📱 View in AR (Quick Look)
                    </a>
                    <button type="button" style={{ ...secondaryBtn, marginTop: 10 }} onClick={generateUsdz} disabled={isExporting}>
                        ↺ Re-export
                    </button>
                </>
            )}
        </div>
    );
};

/* ─── Android view ───────────────────────────────────────────────────── */

const AndroidARView = () => {
    const [status, setStatus] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [glbBlobUrl, setGlbBlobUrl] = useState(null);
    const [glbFile, setGlbFile] = useState(null);
    const blobUrlRef = React.useRef(null);

    React.useEffect(() => {
        return () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); };
    }, []);

    const generateGlb = async () => {
        setIsExporting(true);
        setGlbBlobUrl(null);
        setStatus({ tone: "info", text: "Exporting building — this takes a few seconds…" });
        try {
            const name = (params.building_type_name || "sensei-building")
                .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            const fileName = `${name}-${params.p_w || 0}x${params.p_d || 0}x${params.p_h || 0}.glb`;
            const file = await exportBuildingSceneToGlb(const_var.scene, fileName, const_var.camera?.position);
            if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
            const url = URL.createObjectURL(file);
            blobUrlRef.current = url;
            setGlbFile(file);
            setGlbBlobUrl(url);
            const sizeKb = Math.round(file.size / 1024);
            setStatus({ tone: "success", text: `Ready! (${sizeKb} KB) — Tap "View in AR" in the 3D viewer below.` });
        } catch (err) {
            setStatus({ tone: "error", text: err?.message || "Could not export the building." });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div style={{ padding: "4px 0 8px" }}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
                <div style={{ fontSize: 52 }}>🏗️</div>
                <h5 style={{ margin: "10px 0 4px", fontWeight: 700, color: "#111" }}>
                    View In Your Space
                </h5>
                <p style={{ color: "#555", fontSize: 14, margin: 0 }}>
                    Place your building in the real world using Android AR.
                </p>
            </div>

            {!glbBlobUrl && (
                <button type="button" style={primaryBtn} onClick={generateGlb} disabled={isExporting}>
                    {isExporting ? "Exporting building…" : "🏗️ Prepare for AR"}
                </button>
            )}

            {status && <div style={statusBox(status.tone)}>{status.text}</div>}

            {glbBlobUrl && (
                <>
                    {/* model-viewer handles Android AR via WebXR — works with blob URLs,
                        no hosted file needed. Tap the AR button (cube icon) to launch. */}
                    <div style={{ borderRadius: 12, overflow: "hidden", marginTop: 14, background: "#111" }}>
                        {/* eslint-disable-next-line react/no-unknown-property */}
                        <model-viewer
                            src={glbBlobUrl}
                            ar
                            ar-modes="webxr scene-viewer quick-look"
                            ar-scale="auto"
                            camera-controls
                            auto-rotate
                            shadow-intensity="1"
                            style={{ width: "100%", height: "320px" }}
                        />
                    </div>
                    <div style={{ fontSize: 12, color: "#666", marginTop: 8, textAlign: "center" }}>
                        Tap the <strong>AR cube icon</strong> (bottom-right of viewer) to launch AR
                    </div>
                    <button type="button" style={{ ...secondaryBtn, marginTop: 10 }} onClick={generateGlb} disabled={isExporting}>
                        ↺ Re-export
                    </button>
                    {glbFile && (
                        <button type="button" style={{ ...secondaryBtn, marginTop: 8 }} onClick={() => downloadArModel(glbFile)}>
                            ⬇ Download GLB
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

/* ─── Desktop view ───────────────────────────────────────────────────── */

const DesktopARView = ({ arConfig, qrTargetUrl }) => {
    const [isPreparing, setIsPreparing] = useState(false);
    const [preparedFile, setPreparedFile] = useState(null);
    const [status, setStatus] = useState(null);

    const prepareModel = async () => {
        setIsPreparing(true);
        setStatus(null);
        try {
            const name = (params.building_type_name || "sensei-building")
                .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            const fileName = `${name}-${params.p_w || 0}x${params.p_d || 0}x${params.p_h || 0}.glb`;
            const file = await exportBuildingSceneToGlb(const_var.scene, fileName, const_var.camera?.position);
            setPreparedFile(file);
            setStatus({ tone: "success", text: "Building exported as GLB." });
        } catch (err) {
            setStatus({ tone: "error", text: err?.message || "Could not export the building model." });
        } finally {
            setIsPreparing(false);
        }
    };

    return (
        <div>
            <p style={{ color: "#666", marginTop: 4, marginBottom: 16, fontSize: 14 }}>
                Scan the QR code with your iPhone or Android to launch AR, or export the current building as a 3D file.
            </p>

            <QrCodePreview
                title="Scan on your phone to view in AR"
                targetUrl={qrTargetUrl}
                helperText="Opens the AR viewer on your iPhone (Quick Look) or Android (Scene Viewer)."
            />

            <div style={{ ...cardStyle, marginTop: 14 }}>
                <div style={{ fontWeight: 700, color: "#111", marginBottom: 6 }}>
                    Export 3D Model
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    <button
                        type="button"
                        style={{ ...primaryBtn, width: "auto", marginTop: 0 }}
                        onClick={prepareModel}
                        disabled={isPreparing}
                    >
                        {isPreparing ? "Preparing…" : "Prepare 3D Model"}
                    </button>
                    {preparedFile && (
                        <button
                            type="button"
                            style={{ ...secondaryBtn, width: "auto", marginTop: 0 }}
                            onClick={() => downloadArModel(preparedFile)}
                        >
                            Download GLB
                        </button>
                    )}
                </div>
                {status && <div style={statusBox(status.tone)}>{status.text}</div>}
            </div>

            <div style={{ ...cardStyle, fontSize: 13, color: "#555", marginTop: 0 }}>
                <span style={{ fontWeight: 700, color: "#111" }}>Hosted AR: </span>
                Android {arConfig.androidModelUrl ? "✅" : "❌"}&nbsp;|&nbsp;
                iPhone {arConfig.iosModelUrl ? "✅" : "❌"}
            </div>
        </div>
    );
};

/* ─── Main modal ─────────────────────────────────────────────────────── */

const ViewInYourSpaceModal = ({ show, onHide }) => {
    const leadcustomerState = useSelector((state) => state.leadcustomerReducer || {});
    const arPlatform = useMemo(() => getARPlatform(), []);
    const shareLink = leadcustomerState?.returnRespone?.share_link || "";

    const arConfig = useMemo(
        () =>
            getHostedARModelUrls({
                shareLink,
                buildingTypeName: params.building_type_name,
                width: params.p_w,
                length: params.p_d,
                height: params.p_h,
                manufacturerId: params.m_s_n,
            }),
        [shareLink]
    );

    const qrTargetUrl = useMemo(() => {
        if (typeof window === "undefined") return "";
        const baseUrl = `${window.location.origin}/#/ar-view`;
        const query = new URLSearchParams();
        if (shareLink) query.set("shareLink", shareLink);
        if (params.building_type_name) query.set("buildingTypeName", params.building_type_name);
        if (params.p_w != null) query.set("width", params.p_w);
        if (params.p_d != null) query.set("length", params.p_d);
        if (params.p_h != null) query.set("height", params.p_h);
        if (params.m_s_n != null) query.set("manufacturerId", params.m_s_n);
        return makeUrlMobileReachable(`${baseUrl}?${query.toString()}`);
    }, [shareLink]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            dialogClassName="react-modal"
            backdropClassName="ract-modal-pre-parent"
        >
            <Modal.Body style={{ paddingTop: 28 }}>
                <div className="close" onClick={onHide}>
                    <i className="icon-cross-ico"></i>
                </div>
                <div style={{ padding: "4px 8px 8px" }}>
                    {arPlatform === "ios" && (
                        <IosARView />
                    )}
                    {arPlatform === "android" && (
                        <AndroidARView />
                    )}
                    {arPlatform === "desktop" && (
                        <DesktopARView arConfig={arConfig} qrTargetUrl={qrTargetUrl} />
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ViewInYourSpaceModal;
